import { motion } from "framer-motion";
import { Mail, Clock, Check, Loader2 } from "lucide-react";
import { BRAND } from "@/lib/data";
import { useContactForm } from "@/hooks/useContactForm";
import { Seo } from "@/components/Seo";

const PROJECT_TYPES = ["New Build", "Renovation", "Extension", "Property Development", "Other"];

export default function Contact() {
    const { form, status, errorMsg, update, submit, reset } = useContactForm();

    return (
        <main data-testid="contact-page" className="bg-bone">
            <Seo
                title="Contact Us — Get a Quote | Brazier Building & Design, Central Coast"
                description="Get in touch with Brazier Building & Design for your custom home, renovation or knock down rebuild on the Central Coast NSW. Send an enquiry and we'll be in touch within one business day."
                path="/contact"
            />
            {/* Hero */}
            <section className="relative pt-20 pb-16 md:pt-28 md:pb-24 bg-char text-bone overflow-hidden">
                <div className="absolute inset-0 opacity-20 bg-[radial-gradient(ellipse_at_30%_50%,_var(--clay),_transparent_60%)]" />
                <div className="container-wide relative">
                    <div className="overline text-clay mb-5">Contact</div>
                    <h1 className="font-display text-5xl md:text-7xl font-light tracking-tight leading-[1.05] max-w-3xl">
                        Get in <span className="italic text-clay">Touch.</span>
                    </h1>
                    <p className="mt-6 max-w-xl text-bone/70 text-base md:text-lg">
                        Tell us about your project. We'll be in touch within one business day.
                    </p>
                </div>
            </section>

            {/* Form + contact info */}
            <section className="section-pad">
                <div className="container-wide grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    {/* Form */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-7"
                    >
                        {status === "success" ? (
                            <SuccessPanel onReset={reset} />
                        ) : (
                            <ContactForm
                                form={form}
                                status={status}
                                errorMsg={errorMsg}
                                update={update}
                                submit={submit}
                            />
                        )}
                    </motion.div>

                    {/* Info */}
                    <ContactInfo />
                </div>
            </section>
        </main>
    );
}

function SuccessPanel({ onReset }) {
    return (
        <div
            data-testid="contact-success"
            className="border border-clay/50 bg-clay/5 p-10 md:p-12"
        >
            <div className="w-14 h-14 rounded-full bg-clay text-bone flex items-center justify-center mb-6">
                <Check size={26} strokeWidth={2} />
            </div>
            <h2 className="font-display text-3xl md:text-4xl font-light mb-4">
                Thanks — we've got your enquiry.
            </h2>
            <p className="text-ink2 max-w-md">
                One of the team will be in touch within one business day. In the meantime, feel free to email us at{" "}
                <a href={`mailto:${BRAND.email}`} className="text-clay font-medium">
                    {BRAND.email}
                </a>
                .
            </p>
            <button
                onClick={onReset}
                data-testid="contact-send-another"
                className="btn-ghost mt-10"
            >
                Send another message
            </button>
        </div>
    );
}

function ContactForm({ form, status, errorMsg, update, submit }) {
    return (
        <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Name" testId="contact-input-name">
                    <input
                        required
                        type="text"
                        value={form.name}
                        onChange={update("name")}
                        data-testid="contact-input-name"
                        className="w-full bg-transparent border-b border-ink/20 focus:border-clay outline-none py-3 text-base"
                    />
                </Field>
                <Field label="Phone" testId="contact-input-phone">
                    <input
                        required
                        type="tel"
                        value={form.phone}
                        onChange={update("phone")}
                        data-testid="contact-input-phone"
                        className="w-full bg-transparent border-b border-ink/20 focus:border-clay outline-none py-3 text-base"
                    />
                </Field>
            </div>

            <Field label="Email" testId="contact-input-email">
                <input
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    data-testid="contact-input-email"
                    className="w-full bg-transparent border-b border-ink/20 focus:border-clay outline-none py-3 text-base"
                />
            </Field>

            <Field label="Project Type">
                <select
                    value={form.project_type}
                    onChange={update("project_type")}
                    data-testid="contact-input-project-type"
                    className="w-full bg-transparent border-b border-ink/20 focus:border-clay outline-none py-3 text-base appearance-none cursor-pointer"
                >
                    {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>
                            {t}
                        </option>
                    ))}
                </select>
            </Field>

            <Field label="Tell us about your project">
                <textarea
                    required
                    rows={5}
                    value={form.message}
                    onChange={update("message")}
                    data-testid="contact-input-message"
                    className="w-full bg-transparent border-b border-ink/20 focus:border-clay outline-none py-3 text-base resize-none"
                />
            </Field>

            {status === "error" && (
                <div data-testid="contact-error" className="text-sm text-red-600">
                    {errorMsg}
                </div>
            )}

            <div className="pt-4">
                <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit"
                    className="btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {status === "loading" ? (
                        <>
                            <Loader2 size={16} className="animate-spin" /> Sending…
                        </>
                    ) : (
                        <>Send Enquiry</>
                    )}
                </button>
            </div>
        </form>
    );
}

function ContactInfo() {
    return (
        <div className="lg:col-span-5 space-y-8">
            <InfoRow icon={<Mail size={18} />} label="Email">
                <a href={`mailto:${BRAND.email}`} data-testid="contact-info-email" className="hover:text-clay">
                    {BRAND.email}
                </a>
            </InfoRow>
            <InfoRow icon={<Clock size={18} />} label="Hours">
                {BRAND.hours}
            </InfoRow>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block text-xs tracking-[0.2em] uppercase text-ink2 mb-2">{label}</span>
            {children}
        </label>
    );
}

function InfoRow({ icon, label, children }) {
    return (
        <div className="flex items-start gap-4 border-b border-line pb-6">
            <div className="text-clay shrink-0 mt-1">{icon}</div>
            <div>
                <div className="text-xs tracking-[0.2em] uppercase text-ink2 mb-1">{label}</div>
                <div className="text-base md:text-lg text-ink">{children}</div>
            </div>
        </div>
    );
}
